// BillingCards.jsx
import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { useProfileContext } from "../../../Contexts/ProfileContext";

const PERIODS = {
    ONE_MONTH: "ONE_MONTH",
    SIX_MONTHS: "SIX_MONTHS",
    ONE_YEAR: "ONE_YEAR",
};

function normalizePayRate(payRate) {
    if (!payRate) return PERIODS.ONE_MONTH;
    const v = String(payRate).toUpperCase();
    if (v.includes("YEAR")) return PERIODS.ONE_YEAR;
    if (v.includes("SIX")) return PERIODS.SIX_MONTHS;
    if (v.includes("ONE_MONTH")) return PERIODS.ONE_MONTH;
    // allow "MONTH" or "MONTHLY"
    if (v.includes("MONTH")) return PERIODS.ONE_MONTH;
    return PERIODS.ONE_MONTH;
}

function formatMoney(n) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(n || 0));
}

export default function BillingCards({
    isActive,
    className = "",
    payRate,                 // "ONE_MONTH" | "SIX_MONTHS" | "ONE_YEAR" (any case/label)
    selectedPackage,         // the whole selected package object
    setSelectedPackage,      // (pkg) => void
}) {
    const { t } = useTranslation();
    const { packages = [], companyDetails } = useSelector((s) => s.global);
    const { setIsUpgrade } = useProfileContext();

    const period = normalizePayRate(payRate);

    const paidPackages = useMemo(
        () =>
            (packages || [])
                .filter((p) => Number(p?.basic_price) > 0) // hide Free
                .filter((p) => (p?.show_in_dashboard ?? 1) !== 0),
        [packages]
    );


    const pickPriceRow = (pkg) => {
        const list = pkg?.prices || [];
        let row = list.find((r) => r?.period === period);
        if (!row) row = list.find((r) => r?.period === PERIODS.ONE_MONTH);
        return row;
    };

    const pickPackage = (pkg) => {
        const oldPrice = companyDetails?.package?.prices?.[0]?.price ?? 0
        const newPrice = pkg?.prices?.[0]?.price ?? 0
        if (oldPrice >= newPrice) {
            setIsUpgrade(false)
        } else {
            setIsUpgrade(true)
        }
        setSelectedPackage(pkg)
    };

    return (
        <div className={`w-full max-w-3xl mx-auto ${className}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700">
                {paidPackages.map((pkg) => {
                    const row = pickPriceRow(pkg);
                    const total = Number(row?.price ?? pkg?.basic_price ?? 0);
                    const months = Number(row?.numeric_period || 1);
                    const monthlyEff = total / Math.max(1, months);
                    const discount = Number(row?.discount_percentage || 0);
                    const isSelected = selectedPackage?.id === pkg.id;


                    return (
                        <button
                            key={pkg.id}
                            type="button"
                            disabled={isActive ? companyDetails?.package?.id === pkg.id ? true : false : false}
                            onClick={() => pickPackage(pkg)}
                            aria-pressed={isSelected}
                            className={[
                                "group border-[0.5px] border-gray-100 text-left p-6 md:p-7 transition-colors outline-none",
                                isSelected
                                    ? "bg-[#5659fd] text-white"
                                    : companyDetails?.package?.id === pkg.id ?
                                        "bg-white dark:bg-[#696CFF] text-gray-400 dark:text-zinc-100" :
                                        "bg-white dark:bg-[#696CFF] text-[zinc-900] dark:text-zinc-100",
                            ].join(" ")}
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-sm md:text-base font-semibold opacity-90">{pkg.title}</div>
                                {discount > 0 && period != PERIODS.ONE_MONTH && (
                                    <span
                                        className={[
                                            "text-xs px-2 py-1 rounded-full",
                                            isSelected ? "bg-white/10 text-white" : "bg-zinc-100 dark:bg-zinc-800",
                                        ].join(" ")}
                                    >
                                        {t('payment.save_discount', { discount })}
                                    </span>
                                )}
                            </div>

                            <div className="mt-2 flex items-end gap-1">
                                <span className="text-4xl md:text-5xl font-extrabold leading-none">
                                    ${formatMoney(monthlyEff)}
                                </span>
                                <span className="pb-1 md:pb-1.5 text-base md:text-lg font-medium opacity-90">
                                    {t('payment.per_screen_month')}
                                </span>
                            </div>

                            <div className="mt-2 text-xs md:text-sm opacity-70">
                                {row?.period === PERIODS.ONE_MONTH && t('payment.billed_monthly')}
                                {row?.period === PERIODS.SIX_MONTHS && t('payment.billed_six_months', { total: formatMoney(total) })}
                                {row?.period === PERIODS.ONE_YEAR && t('payment.billed_yearly', { total: formatMoney(total) })}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
